import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Save, ChevronLeft, Loader2, ImagePlus, Send, X, Bold, Italic,
  Heading2, Heading3, Link as LinkIcon, List, Youtube, Video, Image as ImageIcon, Quote
} from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = ["News", "Buying Guide", "Tips", "Reviews", "Market Insights", "Dealer Spotlight"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function extractYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function extractVimeoId(url) {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("write");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const bodyRef = useRef(null);

  const [form, setForm] = useState({
    title: "", slug: "", excerpt: "", body: "", cover_image: "",
    category: "News", tags: [], status: "draft",
  });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate("/login?next=/admin/blog");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (isEdit && user?.role === "admin") {
      api.get(`/admin/blog/posts/${id}`).then(({ data }) => setForm({
        title: data.title || "", slug: data.slug || "", excerpt: data.excerpt || "",
        body: data.body || "", cover_image: data.cover_image || "",
        category: data.category || "News", tags: data.tags || [], status: data.status || "draft",
      }));
    }
  }, [isEdit, id, user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const insertAtCursor = (snippet) => {
    const el = bodyRef.current;
    if (!el) return update("body", form.body + "\n" + snippet);
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    const before = form.body.slice(0, start);
    const after = form.body.slice(end);
    const newBody = before + snippet + after;
    update("body", newBody);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const wrapSelection = (open, close = "") => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = form.body.slice(start, end);
    const before = form.body.slice(0, start);
    const after = form.body.slice(end);
    const closing = close || open;
    const newBody = before + open + (selected || "text") + closing + after;
    update("body", newBody);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + open.length + (selected ? selected.length : 4);
      el.setSelectionRange(pos, pos);
    });
  };

  const uploadCover = async (file) => {
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("cover_image", `${BACKEND_URL}${data.url}`);
      toast.success("Cover uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setCoverUploading(false); }
  };

  const uploadInlineMedia = async (file, type /* image | video */) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND_URL}${data.url}`;
      if (type === "image") {
        const alt = file.name.replace(/\.[^.]+$/, "");
        insertAtCursor(`\n<figure>\n  <img src="${url}" alt="${alt}" />\n  <figcaption>${alt}</figcaption>\n</figure>\n`);
      } else {
        insertAtCursor(`\n<video controls preload="metadata" src="${url}"></video>\n`);
      }
      toast.success(`${type === "image" ? "Image" : "Video"} inserted`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const insertYouTube = () => {
    const url = window.prompt("Paste YouTube URL (any format)");
    if (!url) return;
    const vid = extractYouTubeId(url);
    if (!vid) { toast.error("Could not parse YouTube URL"); return; }
    insertAtCursor(`\n<iframe src="https://www.youtube.com/embed/${vid}" title="YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy"></iframe>\n`);
    toast.success("YouTube embed inserted");
  };

  const insertVimeo = () => {
    const url = window.prompt("Paste Vimeo URL");
    if (!url) return;
    const vid = extractVimeoId(url);
    if (!vid) { toast.error("Could not parse Vimeo URL"); return; }
    insertAtCursor(`\n<iframe src="https://player.vimeo.com/video/${vid}" title="Vimeo video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>\n`);
    toast.success("Vimeo embed inserted");
  };

  const insertLink = () => {
    const url = window.prompt("URL (https://…)");
    if (!url) return;
    const safe = url.replace(/"/g, "&quot;");
    wrapSelection(`<a href="${safe}" target="_blank" rel="noreferrer">`, `</a>`);
  };

  const addTag = () => {
    const v = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (v && !form.tags.includes(v)) update("tags", [...form.tags, v]);
    setTagInput("");
  };

  const save = async (publish = false) => {
    if (!form.title || !form.body) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, status: publish ? "published" : form.status };
      if (isEdit) {
        await api.put(`/admin/blog/posts/${id}`, payload);
        toast.success(publish ? "Post published" : "Post saved");
      } else {
        const { data } = await api.post("/admin/blog/posts", payload);
        toast.success(publish ? "Post published" : "Draft saved");
        navigate(`/admin/blog/edit/${data.id}`);
      }
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!user || user.role !== "admin") return null;

  const toolbarBtn = "w-9 h-9 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 hover:border-cm-accent text-slate-700 flex items-center justify-center transition-colors disabled:opacity-50";

  return (
    <div data-testid="admin-editor-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <Link to="/admin/blog" className="inline-flex items-center gap-1 text-sm text-cm-accent font-medium">
          <ChevronLeft className="w-4 h-4" /> All posts
        </Link>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">{isEdit ? "Edit post" : "New post"}</h1>
          <div className="flex gap-2">
            <button onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn" className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-cm-primary text-sm font-medium flex items-center gap-2 hover:bg-slate-50 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
            </button>
            <button onClick={() => save(true)} disabled={saving} data-testid="publish-btn" className="px-4 py-2.5 rounded-xl bg-cm-accent hover:bg-cm-accent-hover text-white text-sm font-semibold flex items-center gap-2 press-scale disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publish
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 md:p-7">
            <Field label="Title">
              <input data-testid="post-title" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="A captivating headline…" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-base focus:bg-white focus:border-cm-accent outline-none font-display font-semibold" />
            </Field>
            <Field label="Slug (URL)">
              <input data-testid="post-slug" value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="auto-generated from title" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none font-mono" />
            </Field>
            <Field label="Excerpt (used for SEO + social previews)">
              <textarea data-testid="post-excerpt" rows={2} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} placeholder="A 1-2 sentence summary…" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none resize-none" />
            </Field>

            <div className="mt-5 flex gap-1 p-1 bg-slate-100 rounded-xl">
              {[{ id: "write", label: "Write" }, { id: "preview", label: "Preview" }].map(tt => (
                <button key={tt.id} data-testid={`editor-tab-${tt.id}`} type="button" onClick={() => setTab(tt.id)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === tt.id ? 'bg-white text-cm-primary shadow-sm' : 'text-slate-600'}`}>
                  {tt.label}
                </button>
              ))}
            </div>

            {tab === "write" ? (
              <div>
                {/* Toolbar */}
                <div className="mt-3 flex flex-wrap items-center gap-1.5 p-2 bg-slate-50 border border-slate-200 rounded-xl">
                  <button type="button" data-testid="tb-bold" title="Bold" onClick={() => wrapSelection("<strong>", "</strong>")} className={toolbarBtn}><Bold className="w-4 h-4" /></button>
                  <button type="button" data-testid="tb-italic" title="Italic" onClick={() => wrapSelection("<em>", "</em>")} className={toolbarBtn}><Italic className="w-4 h-4" /></button>
                  <span className="w-px h-5 bg-slate-300 mx-1" />
                  <button type="button" data-testid="tb-h2" title="Heading 2" onClick={() => insertAtCursor("\n<h2>Heading</h2>\n")} className={toolbarBtn}><Heading2 className="w-4 h-4" /></button>
                  <button type="button" data-testid="tb-h3" title="Heading 3" onClick={() => insertAtCursor("\n<h3>Subheading</h3>\n")} className={toolbarBtn}><Heading3 className="w-4 h-4" /></button>
                  <button type="button" data-testid="tb-quote" title="Quote" onClick={() => insertAtCursor("\n<blockquote>Quote here</blockquote>\n")} className={toolbarBtn}><Quote className="w-4 h-4" /></button>
                  <button type="button" data-testid="tb-list" title="Bullet list" onClick={() => insertAtCursor("\n<ul>\n  <li>First item</li>\n  <li>Second item</li>\n</ul>\n")} className={toolbarBtn}><List className="w-4 h-4" /></button>
                  <button type="button" data-testid="tb-link" title="Link" onClick={insertLink} className={toolbarBtn}><LinkIcon className="w-4 h-4" /></button>
                  <span className="w-px h-5 bg-slate-300 mx-1" />
                  <label data-testid="tb-image" title="Insert image" className={toolbarBtn + " cursor-pointer"}>
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadInlineMedia(e.target.files[0], "image")} />
                  </label>
                  <label data-testid="tb-video-upload" title="Upload video file" className={toolbarBtn + " cursor-pointer"}>
                    <Video className="w-4 h-4" />
                    <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadInlineMedia(e.target.files[0], "video")} />
                  </label>
                  <button type="button" data-testid="tb-youtube" title="Embed YouTube" onClick={insertYouTube} className={toolbarBtn}><Youtube className="w-4 h-4" /></button>
                  <button type="button" data-testid="tb-vimeo" title="Embed Vimeo" onClick={insertVimeo} className={toolbarBtn + " text-[10px] font-bold"}>V</button>
                  <span className="ml-auto text-xs text-slate-400 hidden sm:inline">HTML supported</span>
                </div>

                <textarea ref={bodyRef} data-testid="post-body" rows={22} value={form.body} onChange={(e) => update("body", e.target.value)}
                  placeholder="<p>Write your article. Use the toolbar to insert images, videos, or YouTube/Vimeo embeds.</p>"
                  className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none font-mono leading-relaxed" />
              </div>
            ) : (
              <div data-testid="post-preview" className="blog-body mt-3 prose prose-slate max-w-none p-5 border border-slate-200 rounded-xl bg-white min-h-[400px] prose-headings:font-display prose-headings:text-cm-primary prose-headings:font-extrabold prose-h2:text-2xl prose-h3:text-xl prose-a:text-cm-accent" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.body || '<p class="text-slate-400">Preview will appear here…</p>', { ADD_TAGS: ["iframe"], ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"] }) }} />
            )}
          </div>

          <aside className="space-y-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-display font-bold text-cm-primary">Cover image</h3>
              {form.cover_image ? (
                <div className="mt-3 relative aspect-[16/10] rounded-xl overflow-hidden bg-slate-100">
                  <img src={form.cover_image} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => update("cover_image", "")} data-testid="remove-cover" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label data-testid="upload-cover" className="mt-3 aspect-[16/10] rounded-xl border-2 border-dashed border-slate-300 hover:border-cm-accent flex flex-col items-center justify-center gap-2 cursor-pointer text-slate-500 hover:text-cm-accent">
                  {coverUploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                  <span className="text-xs font-medium">{coverUploading ? "Uploading…" : "Upload cover"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} />
                </label>
              )}
              <input data-testid="cover-url-input" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} placeholder="…or paste image URL" className="mt-3 w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-cm-accent" />
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <Field label="Category">
                <select data-testid="post-category" value={form.category} onChange={(e) => update("category", e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>

              <Field label="Tags">
                <div className="flex gap-2">
                  <input data-testid="tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Press Enter to add" className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:bg-white focus:border-cm-accent outline-none" />
                  <button type="button" onClick={addTag} className="px-3 py-2 rounded-xl bg-cm-primary text-white text-sm font-medium">Add</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-cm-primary text-xs font-medium">
                      #{t}
                      <button onClick={() => update("tags", form.tags.filter((x) => x !== t))} className="text-slate-500 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </Field>

              <Field label="Status">
                <div className="grid grid-cols-2 gap-2">
                  {[{ id: "draft", label: "Draft" }, { id: "published", label: "Published" }].map(s => (
                    <button key={s.id} type="button" data-testid={`status-${s.id}`} onClick={() => update("status", s.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border-2 ${form.status === s.id ? 'border-cm-accent bg-red-50 text-cm-primary' : 'border-slate-200 text-slate-600'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <div className="bg-cm-primary text-white rounded-2xl p-5 text-xs leading-relaxed">
              <h4 className="font-display font-bold text-sm mb-2">Tips for rich content</h4>
              <ul className="space-y-1 text-white/80">
                <li>• Click the image icon to upload + insert a photo at the cursor</li>
                <li>• Click the YouTube icon and paste any YouTube URL</li>
                <li>• Click the V (Vimeo) icon to embed a Vimeo</li>
                <li>• Click the video icon to upload an MP4 directly</li>
                <li>• Mix paragraphs, headings and media freely — preview tab shows the result</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-4 first:mt-0">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}
